import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { withStyles } from '@material-ui/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import SelfInspectBackButton from '../../pages/self-inspect/SelfInspectBackButton';
import SelfInspectButton from './SelfInspectButton';
import SelfInspectQuestionLabel from './SelfInspectQuestionLabel';
import SelfInspectInfoBox from './SelfInspectInfoBox';

const styles = theme => ({
  //
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  scrollContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    overflow: 'inherit',
  },

  title: {
    fontSize: '2rem',
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: 10,
  },
  subtitle: {
    fontSize: '1.5rem',
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: 10,
  },
  selectAllInstruction: {
    fontSize: '1rem',
    textAlign: 'center',
    fontWeight: 'bold',
    marginTop: 20,
    marginBottom: 20,
    color: 'var(--self-inspect-text-color)',
  },
  imagesContainer: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    overflow: 'auto',
    padding: 15,
    borderRadius: 10,
    position: 'relative',
  },

  imageCardContainer: {
    height: '100%',
    padding: 10,
    display: 'block',
  },

  imageCard: {
    height: '100%',
    position: 'relative',
    background: 'white',
    padding: 15,
    marginBottom: 30,
    borderRadius: 10,
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    boxShadow: '1px 1px 10px lightgray',
  },

  imageContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },

  imageCheckboxImage: {
    maxWidth: '100%',
    maxHeight: 100,
  },

  checkedIcon: {
    position: 'absolute',
    top: -10,
    right: -10,
    fontSize: 35,
    color: 'var(--jmi-green)',
    backgroundColor: 'white',
    borderRadius: '100%',
  },

  imageLabel: {
    // flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    marginTop: 15,
  },

  actionContainer: {
    flexGrow: 0,
    flexBasis: 'auto',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 10,
    paddingBottom: 5,
  },

  bottomScrollIndicatorContainer: {
    left: 0,
    right: 0,
    bottom: 60,
    position: 'absolute',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },

  topScrollIndicatorContainer: {
    left: 0,
    right: 0,
    top: 120,
    position: 'absolute',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },

  scrollIndicator: {
    opacity: '50%',
    preserveAspectRatio: 'none',
    transform: 'scale(2.5)',
  },
});

const BOTTOM_PANEL_TRANSITION = '0.5s all ease-in-out';

const ImageRadios = withStyles(styles)(
  ({
    // form values
    cardNumber,
    questions,
    options,
    questionId,
    title,
    subtitle,
    answerValue,
    //
    // functions
    updateAnswer,
    confirmAnswer,
    //
    // other
    classes,
    selfInspectIntro,
    goBack,
    hideBackButton,
    disableContinueIfNoAnswer = false,

    ...rest
  }) => {
    const [scrollFinished, setScrollFinished] = useState(false);
    const scrollableContainerRef = useRef();

    const hasAnswer = !!answerValue;

    const handleChange = ({ questionId, value }) => {
      updateAnswer({
        cardNumber,
        questionId,
        value,
      });
    };

    const handleContinue = () => {
      if (scrollFinished) {
        confirmAnswer({
          cardNumber,
          questionId,
          answerValue,
        });
      } else {
        const scrollBy = scrollableContainerRef.current.children[0].clientHeight;
        const currentScrollTop = scrollableContainerRef.current.scrollTop;
        scrollableContainerRef.current.scrollTo({ top: currentScrollTop + scrollBy + 30, behavior: 'smooth' });
      }
    };

    const isScrolledToBottom = () => {
      return (
        scrollableContainerRef.current.scrollTop >=
        scrollableContainerRef.current.scrollHeight - scrollableContainerRef.current.clientHeight - 50 // 50 is a buffer
      );
    };

    const checkForEndOfScroll = () => {
      const isAtBottom = isScrolledToBottom();

      setScrollFinished(isAtBottom);
    };

    const renderOptions = () => {
      const optionEls = options.map(({ value, label, image, ...inputProps }) => {
        const isSelected = answerValue === value;

        return (
          <div>
            <input
              //
              type="radio"
              id={value}
              value={value}
              name={questionId.toString()}
              checked={isSelected}
              style={{ display: 'none' }}
              onChange={() => {
                !isSelected && handleChange({ questionId, value });
              }}
            />

            <label for={value} key={questionId} className={classes.imageCardContainer}>
              <div className={classes.imageCard} onClick={() => handleChange({ questionId, value: !value })}>
                <div className={classes.imageContainer}>
                  <img src={image} className={classes.imageCheckboxImage} />
                </div>

                <div className={classes.imageLabel}>{label}</div>

                {isSelected && <CheckCircleIcon className={classes.checkedIcon} />}
              </div>
            </label>
          </div>
        );
      });

      return optionEls;
    };

    return (
      <div className={classes.container}>
        <div className={classes.scrollContainer}>
          <div
            style={{
              width: '100%',
              // maxHeight: hasAnswer ? 0 : '100vh',
              transition: BOTTOM_PANEL_TRANSITION,
              overflow: 'hidden',
            }}
          >
            {!!title && <SelfInspectQuestionLabel isHeader>{title}</SelfInspectQuestionLabel>}

            {!!subtitle && (
              <SelfInspectQuestionLabel
                style={{
                  marginTop: 20,
                  marginBottom: 10,
                }}
              >
                {subtitle}
              </SelfInspectQuestionLabel>
            )}
          </div>

          <div
            //
            ref={ref => {
              if (!!ref) {
                scrollableContainerRef.current = ref;
                checkForEndOfScroll();
              }
            }}
            onScroll={checkForEndOfScroll}
            className={classes.imagesContainer}
          >
            {renderOptions()}
          </div>
        </div>

        <div className={classnames(classes.actionContainer)}>
          {!hideBackButton && <SelfInspectBackButton onClick={goBack} className="flex-1 mr-20" />}

          <SelfInspectButton
            label={
              !scrollFinished
                ? 'Scroll down'
                : disableContinueIfNoAnswer && !hasAnswer
                ? 'Select an option'
                : 'Keep going!'
            }
            onClick={handleContinue}
            // disabled={!scrollFinished}
            disabled={disableContinueIfNoAnswer && !hasAnswer}
            className="flex-1"
          />
        </div>
      </div>
    );
  },
);

ImageRadios.propTypes = {};

ImageRadios.defaultProps = {};

export default ImageRadios;
